/**
 * Shared WMS URL construction. Probed against nowCOAST's GeoServer on
 * 2026-09-16; see docs/phase2-overlays-plan.md section 3.
 *
 * WMS 1.3.0 with `crs=EPSG:3857`, which is what the capabilities advertise and
 * what MapLibre's `{bbox-epsg-3857}` placeholder (minx,miny,maxx,maxy) means.
 * 1.3.0 flips to lat,lon for EPSG:4326 — staying in 3857 keeps that away.
 */

/** 512 px: nowCOAST publishes no tile cache, so every GetMap is a live render. */
export const TILE_SIZE = 512

export interface WmsTarget {
  /** e.g. https://nowcoast.noaa.gov/geoserver/weather_radar/wms */
  base: string
  /** e.g. weather_radar:conus_base_reflectivity_mosaic */
  layerName: string
}

/** The tile template MapLibre expands per tile. `time` omitted for static layers. */
export function getMapTemplate(target: WmsTarget, time?: string): string {
  const p = new URLSearchParams({
    service: 'WMS',
    version: '1.3.0',
    request: 'GetMap',
    layers: target.layerName,
    styles: '',
    crs: 'EPSG:3857',
    width: String(TILE_SIZE),
    height: String(TILE_SIZE),
    format: 'image/png',
    transparent: 'true',
  })
  if (time) p.set('TIME', time)
  // bbox last and unencoded: MapLibre substitutes the literal placeholder, and
  // URLSearchParams would percent-encode the braces.
  return `${target.base}?${p.toString()}&bbox={bbox-epsg-3857}`
}

export function getCapabilitiesUrl(base: string): string {
  return `${base}?service=WMS&version=1.3.0&request=GetCapabilities`
}

export function getLegendGraphicUrl(target: WmsTarget): string {
  const p = new URLSearchParams({
    service: 'WMS',
    version: '1.3.0',
    request: 'GetLegendGraphic',
    format: 'application/json',
    layer: target.layerName,
  })
  return `${target.base}?${p.toString()}`
}
